package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.auth.RoleDto

enum class SettingsGroup(val label: String, val adminOnly: Boolean) {
    Account("Account", adminOnly = false),
    Workspace("Workspace", adminOnly = true),
    SecurityAndData("Security and data", adminOnly = true),
}

data class SettingEntry(
    val label: String,
    val anchor: String,
    val keywords: String = "",
)

data class SettingsPageSpec(
    val id: String,
    val label: String,
    val group: SettingsGroup,
    val minRole: RoleDto?,
    val entries: List<SettingEntry>,
)

data class SettingsHit(
    val page: SettingsPageSpec,
    val entry: SettingEntry,
) {
    val trail: String get() = "${page.group.label} › ${page.label}"
}

object SettingsCatalog {
    const val TOP_ANCHOR = "top"

    private val legacyIds = mapOf("workspace" to "general")

    val pages: List<SettingsPageSpec> = listOf(
        SettingsPageSpec(
            id = "profile",
            label = "Profile",
            group = SettingsGroup.Account,
            minRole = null,
            entries = listOf(
                SettingEntry("Name and email", "identity", "identity display name address"),
                SettingEntry("GitHub account", "connected-accounts", "link connect github login"),
                SettingEntry("Appearance", "appearance", "theme dark light mode system"),
                SettingEntry("Sign out everywhere", "sessions", "log out devices sessions"),
                SettingEntry("Erase my personal data", "danger-zone", "delete gdpr privacy remove erase"),
            ),
        ),
        SettingsPageSpec(
            id = "general",
            label = "General",
            group = SettingsGroup.Workspace,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Workspace name", "workspace", "rename"),
                SettingEntry("Default timezone", "workspace", "time zone"),
                SettingEntry("Sign-in access", "sign-in", "login who can join slack"),
                SettingEntry("Leave keywords", "time-off", "vacation ooo away holiday leave"),
                SettingEntry("Replay onboarding tour", "onboarding", "tour setup welcome"),
            ),
        ),
        SettingsPageSpec(
            id = "members",
            label = "Members",
            group = SettingsGroup.Workspace,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Invite teammates", "people", "add people email invite"),
                SettingEntry("Roles", "people", "owner admin member viewer permissions"),
                SettingEntry("Invite links", "invite-links", "join link"),
                SettingEntry("Transfer ownership", "people", "owner transfer"),
            ),
        ),
        SettingsPageSpec(
            id = "integrations",
            label = "Integrations",
            group = SettingsGroup.Workspace,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Slack", "services", "chat connect"),
                SettingEntry("Linear", "services", "issues connect"),
                SettingEntry("GitHub", "services", "pull requests repositories connect"),
                SettingEntry("Google Calendar", "services", "time off calendar connect"),
                SettingEntry("Anthropic API key", "ai", "claude key model bring your own"),
                SettingEntry("Test my setup", TOP_ANCHOR, "check health run diagnose"),
            ),
        ),
        SettingsPageSpec(
            id = "releases",
            label = "Release notes",
            group = SettingsGroup.Workspace,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Release feed", "release-feed", "merged changes releases page"),
                SettingEntry("Watched repositories", "repositories", "repos github"),
                SettingEntry("Hold-back labels", "hold-back-labels", "hide exclude labels"),
            ),
        ),
        SettingsPageSpec(
            id = "billing",
            label = "Plan and billing",
            group = SettingsGroup.Workspace,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Current plan", TOP_ANCHOR, "usage seats limits free team"),
                SettingEntry("Upgrade", "upgrade", "annual monthly pay checkout team plan"),
                SettingEntry("Manage subscription", "upgrade", "invoice card cancel portal"),
            ),
        ),
        SettingsPageSpec(
            id = "data",
            label = "Data and privacy",
            group = SettingsGroup.SecurityAndData,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Raw activity retention", "retention", "keep delete days purge"),
                SettingEntry("Export workspace data", "export", "download zip backup"),
                SettingEntry("Uninstall from Slack", "danger-zone", "remove bot uninstall"),
                SettingEntry("Delete workspace", "danger-zone", "remove erase delete"),
            ),
        ),
        SettingsPageSpec(
            id = "audit-log",
            label = "Audit log",
            group = SettingsGroup.SecurityAndData,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("Audit log", TOP_ANCHOR, "history events who did export csv"),
            ),
        ),
        SettingsPageSpec(
            id = "logs",
            label = "System logs",
            group = SettingsGroup.SecurityAndData,
            minRole = RoleDto.ADMIN,
            entries = listOf(
                SettingEntry("System logs", TOP_ANCHOR, "errors warnings events"),
            ),
        ),
    )

    fun page(id: String): SettingsPageSpec = pages.first { it.id == id }

    fun visibleTo(role: RoleDto?): List<SettingsPageSpec> =
        pages.filter { page ->
            val minimum = page.minRole
            minimum == null || (role != null && role.satisfies(minimum))
        }

    fun resolve(requested: String?, visible: List<SettingsPageSpec>): String? {
        val wanted = requested?.takeIf { it.isNotBlank() }
        val direct = visible.firstOrNull { it.id == wanted }?.id
        val legacy = wanted?.let(legacyIds::get)?.let { id -> visible.firstOrNull { it.id == id }?.id }
        return direct ?: legacy ?: visible.firstOrNull()?.id
    }

    fun suggestions(visible: List<SettingsPageSpec>, limit: Int = DEFAULT_SUGGESTIONS): List<SettingsHit> =
        visible.flatMap { page -> page.entries.map { SettingsHit(page, it) } }.take(limit)

    fun search(query: String, visible: List<SettingsPageSpec>, limit: Int = DEFAULT_LIMIT): List<SettingsHit> {
        val tokens = query.lowercase().split(Regex("\\s+")).filter { it.isNotBlank() }
        if (tokens.isEmpty()) return suggestions(visible)
        return visible
            .flatMap { page -> page.entries.map { SettingsHit(page, it) } }
            .filter { hit ->
                val haystack = "${hit.entry.label} ${hit.entry.keywords} ${hit.page.label} ${hit.page.group.label}".lowercase()
                tokens.all { it in haystack }
            }
            .sortedBy { hit ->
                val label = hit.entry.label.lowercase()
                when {
                    label.startsWith(tokens.first()) -> 0
                    tokens.all { it in label } -> 1
                    else -> 2
                }
            }
            .take(limit)
    }

    private const val DEFAULT_SUGGESTIONS = 7
    private const val DEFAULT_LIMIT = 8
}

fun RoleDto.satisfies(other: RoleDto): Boolean = rank() >= other.rank()

private fun RoleDto.rank(): Int = when (this) {
    RoleDto.OWNER -> 3
    RoleDto.ADMIN -> 2
    RoleDto.MEMBER -> 1
    RoleDto.VIEWER -> 0
}
